import { useState } from 'react';
import AdminLayout from '@/components/admin/AdminLayout';
import { adminApi } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Database, Download, FileArchive, ShieldCheck, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

const getDownloadFilename = (contentDisposition) => {
    if (!contentDisposition) return null;
    const utfMatch = contentDisposition.match(/filename\*=UTF-8''([^;]+)/i);
    if (utfMatch?.[1]) return decodeURIComponent(utfMatch[1]);
    const basicMatch = contentDisposition.match(/filename="?([^";]+)"?/i);
    return basicMatch?.[1] || null;
};

const getApiErrorMessage = async (error) => {
    const fallback = 'No se pudo generar el backup completo';
    const data = error?.response?.data;
    if (!data) return fallback;
    if (typeof data?.detail === 'string') return data.detail;
    if (data instanceof Blob) {
        try {
            const text = await data.text();
            const parsed = JSON.parse(text);
            return parsed?.detail || fallback;
        } catch {
            return fallback;
        }
    }
    return fallback;
};

export default function AdminBackupPage() {
    const [downloading, setDownloading] = useState(false);

    const downloadBackup = async () => {
        setDownloading(true);
        try {
            const response = await adminApi.backup.downloadComplete();
            const filename = getDownloadFilename(response.headers?.['content-disposition'])
                || `kche-backup-completo-${new Date().toISOString().replace(/[:.]/g, '-')}.zip`;
            const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/zip' }));
            const anchor = document.createElement('a');
            anchor.href = url;
            anchor.download = filename;
            document.body.appendChild(anchor);
            anchor.click();
            anchor.remove();
            window.URL.revokeObjectURL(url);
            toast.success('Backup completo generado y descargado');
        } catch (error) {
            const message = await getApiErrorMessage(error);
            toast.error(message);
        } finally {
            setDownloading(false);
        }
    };

    return (
        <AdminLayout title="Backup completo">
            <div className="max-w-4xl space-y-5">
                <div className="rounded-2xl border border-border bg-white p-6 shadow-[var(--shadow-soft)]">
                    <div className="flex flex-col gap-5 md:flex-row md:items-start md:justify-between">
                        <div className="flex gap-4">
                            <div className="flex size-12 shrink-0 items-center justify-center rounded-xl bg-[hsl(var(--kche-cyan))]/10 text-[hsl(var(--kche-cyan))]">
                                <Database className="size-6" />
                            </div>
                            <div>
                                <h2 className="text-lg font-semibold">Copia integral del sitio</h2>
                                <p className="mt-1 max-w-2xl text-sm leading-6 text-muted-foreground">
                                    Genera un ZIP con toda la base MongoDB, archivos e imágenes guardados en GridFS,
                                    usuarios, contenido, configuración, leads, reservas, analítica, registros de actividad
                                    y una copia del código fuente disponible en el despliegue.
                                </p>
                            </div>
                        </div>
                        <Button
                            type="button"
                            onClick={downloadBackup}
                            disabled={downloading}
                            className="shrink-0"
                            data-testid="download-complete-backup"
                        >
                            {downloading ? <Loader2 className="mr-2 size-4 animate-spin" /> : <Download className="mr-2 size-4" />}
                            {downloading ? 'Generando…' : 'Descargar backup'}
                        </Button>
                    </div>
                </div>

                <div className="grid gap-4 md:grid-cols-3">
                    <div className="rounded-2xl border border-border bg-white p-5 shadow-[var(--shadow-soft)]">
                        <FileArchive className="size-5 text-[hsl(var(--kche-cyan))]" />
                        <h3 className="mt-3 font-semibold">Datos + archivos</h3>
                        <p className="mt-1 text-sm leading-6 text-muted-foreground">
                            Incluye todas las colecciones MongoDB y GridFS en BSON para conservar tipos y binarios sin pérdida.
                        </p>
                    </div>
                    <div className="rounded-2xl border border-border bg-white p-5 shadow-[var(--shadow-soft)]">
                        <Database className="size-5 text-[hsl(var(--kche-cyan))]" />
                        <h3 className="mt-3 font-semibold">Código del sitio</h3>
                        <p className="mt-1 text-sm leading-6 text-muted-foreground">
                            Añade frontend, backend y archivos de configuración disponibles en el servidor para recuperación técnica.
                        </p>
                    </div>
                    <div className="rounded-2xl border border-border bg-white p-5 shadow-[var(--shadow-soft)]">
                        <ShieldCheck className="size-5 text-[hsl(var(--kche-cyan))]" />
                        <h3 className="mt-3 font-semibold">Seguro por diseño</h3>
                        <p className="mt-1 text-sm leading-6 text-muted-foreground">
                            No descarga archivos .env ni secretos del hosting. El ZIP incluye manifiesto, hashes e instrucciones de restauración.
                        </p>
                    </div>
                </div>

                <div className="rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm leading-6 text-amber-900">
                    Guarda el ZIP en una ubicación segura. Los servicios externos enlazados por la web (redes sociales,
                    mapas y recursos alojados por terceros) se registran como URLs, pero su contenido externo no puede
                    copiarse de forma garantizada desde este sistema.
                </div>
            </div>
        </AdminLayout>
    );
}
